import React, {
  useEffect, useRef, useState,
} from "react"
import types from "prop-types"
import clsx from "clsx"
import { useOnClickOutside } from "hooks"
import GlobalHeaderNavItemContent from "./GlobalHeaderNavItemContent"

const GlobalHeaderNavDropdownItem = ({
  title, icon, options, isActiveTab, index, tabRef, navIsHorizontal, handleKeyDown, onOptionClick,
}) => {
  const itemId = `item-${title}`
  const menuId = `menu-${title}`
  const containerRef = useRef()
  const triggerRef = useRef()
  const optionRefs = useRef([])
  const [dropdownOpen, setDropdownOpen] = useState(false)

  const closeDropdown = () => setDropdownOpen(false)
  const toggleDropdown = () => setDropdownOpen((open) => !open)

  const closeDropdownAndFocusTrigger = () => {
    closeDropdown()
    triggerRef.current.focus()
  }

  useOnClickOutside(containerRef, closeDropdown)

  useEffect(() => {
    if (dropdownOpen && optionRefs.current[0]) {
      optionRefs.current[0].focus()
    }
  }, [dropdownOpen])

  const handleTriggerKeyDown = (e) => {
    if (e.key === "Escape" && dropdownOpen) {
      e.preventDefault()
      closeDropdown()
      return
    }

    handleKeyDown(e, index)
  }

  const handleOptionKeyDown = (e, optionIndex) => {
    const lastOptionIndex = options.length - 1

    const focusNextOption = () => optionRefs.current[optionIndex === lastOptionIndex ? 0 : optionIndex + 1].focus()
    const focusPreviousOption = () => optionRefs.current[optionIndex === 0 ? lastOptionIndex : optionIndex - 1].focus()

    const keyActions = {
      ArrowDown: focusNextOption,
      ArrowRight: focusNextOption,
      ArrowUp: focusPreviousOption,
      ArrowLeft: focusPreviousOption,
      Home: () => optionRefs.current[0].focus(),
      End: () => optionRefs.current[lastOptionIndex].focus(),
      Escape: () => closeDropdownAndFocusTrigger(),
    }

    if (keyActions[e.key]) {
      e.preventDefault()
      keyActions[e.key]()
    }
  }

  const handleDropdownBlur = (e) => {
    if (!containerRef.current.contains(e.relatedTarget)) {
      closeDropdown()
    }
  }

  return (
    <li
      role="presentation"
      ref={containerRef}
      className="activity-hub-main-nav-list-item-dropdown"
      onBlur={handleDropdownBlur}
    >
      <button
        type="button"
        className={clsx("activity-hub-main-nav-list-item", isActiveTab && "active")}
        tabIndex={isActiveTab ? 0 : -1}
        onClick={toggleDropdown}
        onKeyDown={handleTriggerKeyDown}
        ref={(el) => {
          tabRef.current[index] = el
          triggerRef.current = el
        }}
        aria-label={title}
        aria-haspopup={navIsHorizontal ? "menu" : undefined}
        aria-expanded={dropdownOpen}
        aria-controls={dropdownOpen ? menuId : undefined}
        role={navIsHorizontal ? "tab" : "menuitem"}
      >
        <GlobalHeaderNavItemContent icon={icon} isActiveTab={isActiveTab} title={title} itemId={itemId} />
      </button>
      {
        dropdownOpen && (
          <ul
            id={menuId}
            className="activity-hub-main-nav-list-item-dropdown-menu"
            role={navIsHorizontal ? "menu" : undefined}
            aria-label={navIsHorizontal ? title : undefined}
          >
            {
              options.map((option, optionIndex) => (
                <li key={option.title} role={navIsHorizontal ? "none" : undefined}>
                  <a
                    className="activity-hub-main-nav-list-item-dropdown-menu-item"
                    href={option.path}
                    tabIndex={-1}
                    role={navIsHorizontal ? "menuitem" : undefined}
                    onKeyDown={(e) => handleOptionKeyDown(e, optionIndex)}
                    onClick={(e) => {
                      onOptionClick(e)
                      closeDropdown()
                    }}
                    ref={(el) => {
                      optionRefs.current[optionIndex] = el
                    }}
                  >
                    {option.title}
                  </a>
                </li>
              ))
            }
          </ul>
        )
      }
    </li>
  )
}

GlobalHeaderNavDropdownItem.propTypes = {
  title: types.string.isRequired,
  icon: types.string.isRequired,
  options: types.arrayOf(types.shape({
    title: types.string.isRequired,
    path: types.string.isRequired,
  })).isRequired,
  isActiveTab: types.bool.isRequired,
  index: types.number.isRequired,
  tabRef: types.shape({ current: types.array }).isRequired,
  navIsHorizontal: types.bool.isRequired,
  handleKeyDown: types.func.isRequired,
  onOptionClick: types.func.isRequired,
}

export default GlobalHeaderNavDropdownItem
